import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';

const Legal = () => {
  const [activeTab, setActiveTab] = useState('terms');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
          >
            <div className="w-9 h-9 rounded-xl bg-primary text-white flex items-center justify-center">
              <ShoppingBag className="w-5 h-5" />
            </div>

            <span className="text-xl font-bold text-gray-900">
              BigCircle
            </span>
          </Link>

          {/* Back Button */}
          <button
            type="button"
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white border-b">
        <div className="container mx-auto px-4 py-10 sm:py-14 max-w-4xl">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              {activeTab === 'terms' ? (
                <FileText className="w-7 h-7" />
              ) : (
                <ShieldCheck className="w-7 h-7" />
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-primary mb-1">
                BigCircle Legal
              </p>

              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                {activeTab === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h1>

              <p className="mt-2 text-gray-500">
                {activeTab === 'terms'
                  ? 'Please read these terms carefully before using BigCircle.'
                  : 'Learn how BigCircle collects, uses, and protects your information.'}
              </p>

              <p className="mt-3 text-xs text-gray-400">
                Effective Date: August 26, 2026
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5 flex">
          <button
            type="button"
            onClick={() => setActiveTab('terms')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
              activeTab === 'terms'
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-4 h-4" />
            Terms of Service
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('privacy')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
              activeTab === 'privacy'
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Privacy Policy
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-6 sm:py-10 max-w-4xl">
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-10 lg:p-12">
          {activeTab === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-6 mt-4">
        <p className="text-center text-xs text-gray-400">
          © 2026 BigCircle. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

/* =========================================================
   TERMS OF SERVICE
========================================================= */

const TermsContent = () => {
  return (
    <div className="space-y-10 text-gray-700 leading-relaxed">

      <section>
        <SectionTitle>1. About BigCircle</SectionTitle>
        <p>
          BigCircle is an online marketplace connecting customers with participating vendors and
          delivery personnel.
        </p>
        <p className="mt-3">
          Users can browse products, discover vendors, place orders, provide delivery details,
          make payments, and track orders through BigCircle.
        </p>
      </section>

      <section>
        <SectionTitle>2. Acceptance of These Terms</SectionTitle>
        <p>
          By creating an account, using the Platform, placing orders, or registering as a vendor,
          you agree to comply with these Terms of Service.
        </p>
        <p className="mt-3">
          If you disagree with these Terms, we kindly ask you to refrain from using our services.
        </p>
      </section>

      <section>
        <SectionTitle>3. Eligibility</SectionTitle>
        <p>
          You must provide accurate and current information when registering and maintaining your account.
        </p>
        <BulletList
          items={[
            'Provide truthful and complete registration details.',
            'Protect the confidentiality of your account credentials.',
            'Avoid using another person’s account without permission.',
            'Use BigCircle only for lawful and authorized purposes.',
          ]}
        />
      </section>

      <section>
        <SectionTitle>4. Customer Accounts</SectionTitle>
        <p>
          Access to BigCircle requires an account. You are responsible for protecting your login details
          and activity performed through your account.
        </p>
        <p className="mt-3">
          Please contact BigCircle immediately if you suspect unauthorized access to your account.
        </p>
      </section>

      <section>
        <SectionTitle>5. KYC (Know Your Customer) Process</SectionTitle>
        <p>
          Identity verification may be required before certain account features become accessible.
        </p>
        <p className="mt-3">
          You are responsible for providing accurate contact details, valid identification, and completing
          any verification steps when requested.
        </p>
      </section>

      <section>
        <SectionTitle>6. Orders</SectionTitle>
        <p>
          You may place orders with participating vendors via BigCircle.
        </p>
        <p className="mt-3">
          Order acceptance depends on availability and vendor discretion. Orders may be rejected or canceled due to various factors.
        </p>
      </section>

      <section>
        <SectionTitle>7. Products and Vendors</SectionTitle>
        <p>
          Vendors manage product listings, descriptions, pricing, availability, packaging, and compliance with applicable laws.
        </p>
        <p className="mt-3">
          Product images are for reference purposes and may not perfectly reflect actual products.
        </p>
      </section>

      <section>
        <SectionTitle>8. Prices and Payments</SectionTitle>
        <p>
          Prices may change. Applicable totals are provided before order submission.
        </p>
        <p className="mt-3">
          Payments are processed by third-party providers subject to their own terms, fees, and privacy policies.
        </p>
      </section>

      <section>
        <SectionTitle>9. Delivery</SectionTitle>
        <p>
          Delivery times are estimated and may be affected by traffic, weather, vendor preparation, or other uncontrollable factors.
        </p>
        <p className="mt-3">
          Customers must provide accurate delivery addresses and be reasonably available to receive orders.
        </p>
      </section>

      <section>
        <SectionTitle>10. Cancellations and Refunds</SectionTitle>
        <p>
          Eligibility for cancellations and refunds depends on order status and circumstances.
        </p>
        <p className="mt-3">
          For issues with orders, please contact BigCircle. We may investigate before issuing refunds or adjustments.
        </p>
      </section>

      <section>
        <SectionTitle>11. Prohibited Activities</SectionTitle>
        <p>You may not use BigCircle to:</p>
        <BulletList
          items={[
            'Commit fraud or misrepresent yourself.',
            'Create fraudulent or multiple accounts.',
            'Attempt unauthorized access to the platform.',
            'Upload harmful or malicious software.',
            'Harass other users, vendors, delivery personnel, or staff.',
            'Abuse promotions or platform functions.',
            'Engage in any unlawful activity.',
          ]}
        />
      </section>

      <section>
        <SectionTitle>12. Account Suspension</SectionTitle>
        <p>
          BigCircle may suspend or terminate accounts for suspected fraud, security risks, term violations, or unlawful activities.
        </p>
      </section>

      <section>
        <SectionTitle>13. Platform Availability</SectionTitle>
        <p>
          BigCircle strives for reliability but does not guarantee uninterrupted, error-free, or secure service at all times.
        </p>
      </section>

      <section>
        <SectionTitle>14. Changes to These Terms</SectionTitle>
        <p>
          We may update these Terms periodically. Significant changes will be communicated via platform notifications, email, or other suitable channels.
        </p>
      </section>

      <section>
        <SectionTitle>15. Governing Law</SectionTitle>
        <p>
          These Terms are governed by the laws of the Republic of the Philippines, subject to mandatory legal provisions.
        </p>
      </section>

      <ContactSection
        title="16. Contact Us"
        email="support@bigcircle.ph"
        address="Roxas St. Poblacion, Manukan, Zamboanga del Norte 7110, Philippines"
        phone="+63 9099 148 245"
        facebook="https://www.facebook.com/profile.php?id=61593698674382"
      />
    </div>
  );
};

/* =========================================================
   PRIVACY POLICY
========================================================= */

const PrivacyContent = () => {
  return (
    <div className="space-y-10 text-gray-700 leading-relaxed">

      {/* Privacy commitment */}
      <div className="rounded-2xl bg-primary/5 border border-primary/10 p-5 sm:p-6">
        <div className="flex gap-4">
          <LockIcon />

          <div>
            <h2 className="font-bold text-gray-900">
              Our Privacy Commitment
            </h2>

            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              BigCircle is dedicated to responsibly handling your personal information
              and using it only to provide our services and operate the platform.
            </p>
          </div>
        </div>
      </div>

      <section>
        <SectionTitle>1. Information We Collect</SectionTitle>
        <p>
          We collect information necessary to provide and improve your BigCircle experience,
          depending on how you use our platform.
        </p>
      </section>

      <section>
        <SectionTitle>2. Account Information</SectionTitle>
        <p>When you register an account, we collect:</p>
        <BulletList
          items={[
            'Full name',
            'Email address',
            'Phone number',
            'Authentication credentials',
          ]}
        />
      </section>

      <section>
        <SectionTitle>3. Delivery Information</SectionTitle>
        <p>
          To fulfill your orders, we collect delivery details, including:
        </p>
        <BulletList
          items={[
            'Street or sitio address',
            'Barangay',
            'City or municipality',
            'Additional delivery instructions',
          ]}
        />
      </section>

      <section>
        <SectionTitle>4. Order Information</SectionTitle>
        <p>We collect order-related data such as:</p>
        <BulletList
          items={[
            'Products ordered',
            'Vendor details',
            'Order amounts and dates',
            'Order status and delivery info',
            'Payment status and discounts',
          ]}
        />
      </section>

      <section>
        <SectionTitle>5. Payment Information</SectionTitle>
        <p>
          Payments are processed by third-party providers. BigCircle may receive
          transaction details required for payment confirmation without storing full payment credentials.
        </p>
      </section>

      <section>
        <SectionTitle>6. How We Use Your Information</SectionTitle>
        <p>We utilize your information to:</p>
        <BulletList
          items={[
            'Manage accounts and authenticate users',
            'Verify user identity',
            'Process orders and coordinate deliveries',
            'Handle payments and offer customer support',
            'Improve our services and safeguard security',
            'Prevent fraud and comply with legal obligations',
          ]}
        />
      </section>

      <section>
        <SectionTitle>7. Sharing Information</SectionTitle>
        <p>Information may be shared as necessary:</p>

        <SubTitle>Delivery Personnel</SubTitle>
        <p>Delivery details are shared with assigned drivers to complete orders.</p>

        <SubTitle>Service Providers</SubTitle>
        <p>
          Third parties may assist with hosting, payments, email, security, analytics,
          and other essential services.
        </p>

        <SubTitle>Legal Requirements</SubTitle>
        <p>
          We comply with lawful requests or requirements to disclose information.
        </p>
      </section>

      <section>
        <SectionTitle>8. Information Security</SectionTitle>
        <p>
          BigCircle employs technical and organizational security measures including:
        </p>
        <BulletList
          items={[
            'Strong authentication controls',
            'Password protection',
            'Encrypted connections',
            'Access limitations',
            'Server security protocols',
            'Security monitoring and logging',
          ]}
        />
      </section>

      <section>
        <SectionTitle>9. Data Retention</SectionTitle>
        <p>
          We keep personal information only as long as necessary to deliver services,
          meet legal requirements, prevent fraud, and resolve disputes.
        </p>
      </section>

      <section>
        <SectionTitle>10. Your Privacy Rights</SectionTitle>
        <p>Where applicable, you may have rights to:</p>
        <BulletList
          items={[
            'Access your personal data',
            'Correct inaccuracies',
            'Request deletion in legal contexts',
            'Object to or limit processing',
            'Withdraw consent where allowed',
          ]}
        />
      </section>

      <section>
        <SectionTitle>11. Account Deletion</SectionTitle>
        <p>
          You can request account deletion by contacting us. We may retain certain data required
          for legal or legitimate business purposes such as financial records and fraud prevention.
        </p>
      </section>

      <section>
        <SectionTitle>12. Cookies</SectionTitle>
        <p>
          Necessary cookies and browser storage maintain your guest cart, sign-in, security, and cookie preferences. The guest cart cookie is retained for up to 180 days. Optional visitor analytics only runs after you allow it in Cookie settings. Available Google/Facebook sign-in methods work independently of analytics preferences and may use their own cookies when used. Analytics identifies this browser and records visits and signed-in usage. Choices are saved on this browser for 180 days and can be changed using the Cookie settings button. Turning analytics off stops future visit tracking, but does not erase existing records or third-party cookies.
        </p>
      </section>

      <section>
        <SectionTitle>13. Third-Party Services</SectionTitle>
        <p>
          BigCircle integrates with third-party services that have their own privacy policies.
          Please review their policies when using such services through our platform.
        </p>
      </section>

      <section>
        <SectionTitle>14. Philippine Data Privacy</SectionTitle>
        <p>
          We comply with the Republic of the Philippines’ Data Privacy Act of 2012
          (Republic Act No. 10173) and applicable privacy laws.
        </p>
      </section>

      <section>
        <SectionTitle>15. Changes to This Privacy Policy</SectionTitle>
        <p>
          Updates to this Privacy Policy will be communicated through our platform,
          email, or other appropriate methods.
        </p>
      </section>

      <ContactSection
        title="16. Contact Us"
        name="BigCircle"
        address="Roxas St. Poblacion, Manukan, Zamboanga del Norte 7110, Philippines"
        email="support@bigcircle.ph"
        phone="+63 9099 148 245"
        facebook="https://www.facebook.com/profile.php?id=61593698674382"
      />
    </div>
  );
};

/* =========================================================
   REUSABLE COMPONENTS
========================================================= */

const SectionTitle = ({ children }) => (
  <h2 className="text-xl font-bold text-gray-900 mb-3">{children}</h2>
);

const SubTitle = ({ children }) => (
  <h3 className="mt-5 mb-2 font-semibold text-gray-900">{children}</h3>
);

const BulletList = ({ items }) => (
  <ul className="mt-4 list-disc pl-6 space-y-2">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const ContactSection = ({
  title,
  name = "BigCircle",
  email,
  address,
  phone,
  facebook,
}) => (
  <section className="pt-6 border-t">
    <SectionTitle>{title}</SectionTitle>

    <p>If you have questions, requests, or concerns, please contact {name}:</p>

    <div className="mt-4 rounded-2xl bg-gray-50 p-5">
      <p className="font-semibold text-gray-900">{name}</p>
      {email && <p className="mt-1">Email: <a href={`mailto:${email}`} className="text-primary underline">{email}</a></p>}
      {address && <p>Address: {address}</p>}
      {phone && <p>Phone: <a href={`tel:${phone}`} className="text-primary underline">{phone}</a></p>}
      {facebook && <p>Facebook: <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-primary underline">Facebook Page</a></p>}
    </div>
  </section>
);

const LockIcon = () => (
  <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
);

export default Legal;
